<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="sms-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">{{ $t('email.title') }}</h1>
      </div>
    </div>
    <v-tabs v-model="activeTab" background-color="transparent" class="mb-4 sms-tabs">
      <v-tab>
        <v-icon left>mdi-send</v-icon>
        {{ $t('email.sendEmail') }}
      </v-tab>
      <v-tab>
        <v-icon left>mdi-history</v-icon>
        {{ $t('email.sendHistory') }}
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" touchless>
      <!-- ==================== TAB 1: SEND EMAIL ==================== -->
      <v-tab-item>
        <v-card>
          <v-card-title>
            <v-icon left>mdi-email-outline</v-icon>
            {{ $t('email.sendEmail') }}
          </v-card-title>
          <v-card-text>
            <v-radio-group v-model="sendType" row class="mb-4">
              <v-radio :label="$t('email.singlePerson')" value="SINGLE"></v-radio>
              <v-radio :label="$t('email.bulkSend')" value="BULK"></v-radio>
            </v-radio-group>

            <!-- ===== SINGLE MODE ===== -->
            <template v-if="sendType === 'SINGLE'">
              <v-autocomplete
                v-model="singleForm.recipient"
                :items="recipientSearchResults"
                :search-input.sync="recipientSearch"
                :loading="searchingRecipients"
                item-text="name"
                item-value="id"
                return-object
                :label="$t('email.recipient')"
                prepend-icon="mdi-account-search"
                :placeholder="$t('email.searchByNameOrEmail')"
                :no-data-text="$t('email.noResults')"
                clearable
                outlined
                @change="onRecipientSelected"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <v-text-field
                v-model="singleForm.email"
                :label="$t('email.email')"
                prepend-icon="mdi-email"
                outlined
                :rules="[v => !!v || $t('email.emailRequired')]"
              ></v-text-field>
            </template>

            <!-- ===== BULK MODE ===== -->
            <template v-if="sendType === 'BULK'">
              <v-select
                v-model="bulkForm.filterType"
                :items="bulkFilterOptions"
                :label="$t('email.targetGroup')"
                prepend-icon="mdi-account-group"
                outlined
                @change="previewBulkRecipients"
              ></v-select>

              <v-select
                v-if="bulkForm.filterType === 'DEPARTMENT'"
                v-model="bulkForm.departmentId"
                :items="departments"
                item-text="name"
                item-value="id"
                :label="$t('email.department')"
                prepend-icon="mdi-office-building"
                outlined
                @change="previewBulkRecipients"
              ></v-select>

              <v-autocomplete
                v-if="bulkForm.filterType === 'MANUAL'"
                v-model="bulkForm.manualRecipients"
                :items="recipientSearchResults"
                :search-input.sync="recipientSearch"
                :loading="searchingRecipients"
                item-text="name"
                item-value="id"
                return-object
                :label="$t('email.selectPeople')"
                prepend-icon="mdi-account-multiple-plus"
                multiple
                chips
                deletable-chips
                outlined
                :no-data-text="$t('email.typeToSearch')"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <v-alert v-if="bulkPreview !== null" type="info" outlined class="mb-4">
                <strong>{{ $t('email.recipientsCount') }}: {{ bulkPreview }} {{ $t('email.people') }}</strong>
              </v-alert>
            </template>

            <!-- ===== SUBJECT + MESSAGE (shared) ===== -->
            <v-text-field
              v-model="emailForm.subject"
              :label="$t('email.subject')"
              prepend-icon="mdi-format-title"
              outlined
              counter="200"
              :rules="[v => !!v || $t('email.subjectRequired'), v => (v || '').length <= 200 || $t('email.maxChars')]"
            ></v-text-field>

            <v-textarea
              v-model="emailForm.message"
              :label="$t('email.emailMessage')"
              prepend-icon="mdi-message-text"
              outlined
              rows="8"
              :rules="[v => !!v || $t('email.contentRequired')]"
            ></v-textarea>

            <!-- Actions -->
            <div class="d-flex">
              <v-btn
                v-if="sendType === 'SINGLE'"
                color="primary"
                :loading="sending"
                :disabled="!canSendSingle"
                @click="sendSingle"
                large
              >
                <v-icon left>mdi-send</v-icon>
                {{ $t('email.send') }}
              </v-btn>
              <v-btn
                v-if="sendType === 'BULK'"
                color="primary"
                :loading="sending"
                :disabled="!canSendBulk"
                @click="confirmBulkSend"
                large
              >
                <v-icon left>mdi-send</v-icon>
                {{ $t('email.sendTo') }} {{ bulkPreview || 0 }} {{ $t('email.people') }}
              </v-btn>
              <v-btn text class="ml-2" @click="clearForm" large>{{ $t('email.clear') }}</v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- ==================== TAB 2: HISTORY ==================== -->
      <v-tab-item>
        <v-card outlined class="rounded-lg" style="overflow: hidden;">
          <v-card-text>
            <v-row dense class="mb-2">
              <v-col cols="12" md="3">
                <v-select
                  v-model="historyFilters.type"
                  :items="[{ text: $t('email.all'), value: '' }, { text: $t('email.single'), value: 'SINGLE' }, { text: $t('email.bulk'), value: 'BULK' }]"
                  :label="$t('notificationLogs.type')"
                  outlined dense
                  @change="loadHistory"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="historyFilters.status"
                  :items="statusFilterOptions"
                  :label="$t('common.status')"
                  outlined dense
                  @change="loadHistory"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="historyFilters.search"
                  :label="$t('common.search')"
                  prepend-icon="mdi-magnify"
                  outlined dense clearable
                  @input="loadHistory"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-data-table
              :headers="historyHeaders"
              :items="historyItems"
              :loading="loadingHistory"
              :options.sync="historyOptions"
              :server-items-length="historyTotal"
              class="elevation-0"
              dense
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-icon left color="primary">mdi-history</v-icon>
                  <v-toolbar-title>{{ $t('email.emailHistory') }}</v-toolbar-title>
                </v-toolbar>
              </template>
              <template v-slot:[`item.createdAt`]="{ item }">
                {{ new Date(item.createdAt).toLocaleString('pl-PL') }}
              </template>
              <template v-slot:[`item.type`]="{ item }">
                <v-chip x-small :color="item.type === 'SINGLE' ? 'blue' : 'purple'" text-color="white">
                  {{ item.type === 'SINGLE' ? $t('email.single') : $t('email.bulk') }}
                </v-chip>
              </template>
              <template v-slot:[`item.recipients`]="{ item }">
                <span v-if="item.type === 'SINGLE'">{{ getFirstRecipientName(item) }}</span>
                <span v-else>{{ item.totalRecipients }} {{ $t('email.people') }}</span>
              </template>
              <template v-slot:[`item.subject`]="{ item }">
                {{ (item.subject || '').length > 40 ? item.subject.substring(0, 40) + '...' : item.subject }}
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip x-small :color="getStatusColor(item.status)" text-color="white">
                  {{ getStatusText(item.status) }}
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn x-small color="primary" @click="showMessageDetail(item.id)" class="mr-1">
                  <v-icon x-small>mdi-eye</v-icon>
                </v-btn>
                <v-btn v-if="item.failedCount > 0" x-small color="warning" @click="resendMessage(item.id)">
                  <v-icon x-small>mdi-refresh</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Message Detail Dialog -->
        <v-dialog content-class="dark-form-dialog" v-model="detailDialog" max-width="700">
          <v-card v-if="detailMessage">
            <v-card-title>
              {{ $t('email.messageDetails') }}
              <v-spacer></v-spacer>
              <v-chip small :color="getStatusColor(detailMessage.status)" text-color="white">
                {{ getStatusText(detailMessage.status) }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <div class="mb-3">
                <strong>{{ $t('common.date') }}:</strong> {{ new Date(detailMessage.createdAt).toLocaleString('pl-PL') }}
              </div>
              <div class="mb-3">
                <strong>{{ $t('email.sender') }}:</strong> {{ detailMessage.sender && detailMessage.sender.firstName }} {{ detailMessage.sender && detailMessage.sender.lastName }}
              </div>
              <div class="mb-3">
                <strong>{{ $t('email.subject') }}:</strong> {{ detailMessage.subject }}
              </div>
              <div class="mb-3">
                <strong>{{ $t('email.content') }}:</strong>
                <v-card outlined class="pa-3 mt-1" style="white-space: pre-wrap;">{{ detailMessage.content }}</v-card>
              </div>
              <div class="mb-3">
                <strong>{{ $t('email.recipientsCount') }} ({{ detailMessage.totalRecipients }}):</strong>
                <v-simple-table dense class="mt-1">
                  <thead>
                    <tr><th>{{ $t('email.person') }}</th><th>{{ $t('email.email') }}</th><th>{{ $t('common.status') }}</th><th>{{ $t('common.error') }}</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, idx) in detailMessage.recipients" :key="idx">
                      <td>{{ r.name }}</td>
                      <td>{{ r.email }}</td>
                      <td>
                        <v-chip x-small :color="r.status === 'SENT' ? 'success' : r.status === 'FAILED' ? 'error' : 'grey'" text-color="white">
                          {{ r.status }}
                        </v-chip>
                      </td>
                      <td class="error--text">{{ r.error || '—' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="detailDialog = false">{{ $t('common.close') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-tab-item>
    </v-tabs-items>

    <!-- Bulk confirm dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="bulkConfirmDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('email.bulkConfirmTitle') }}</v-card-title>
        <v-card-text>
          <v-alert type="warning" outlined>
            {{ $t('email.bulkConfirmText', { count: bulkPreview }) }}
          </v-alert>
          <div class="mt-2"><strong>{{ $t('email.subject') }}:</strong> {{ emailForm.subject }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="bulkConfirmDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :loading="sending" @click="sendBulk">{{ $t('email.confirmAndSend') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");

export default {
  name: "EmailPage",
  data() {
    return {
      activeTab: 0,
      sendType: "SINGLE",
      sending: false,
      searchingRecipients: false,
      recipientSearch: "",
      recipientSearchResults: [],
      searchTimeout: null,

      singleForm: { recipient: null, email: "" },
      bulkForm: { filterType: null, departmentId: null, manualRecipients: [] },
      bulkPreview: null,
      bulkConfirmDialog: false,
      departments: [],

      bulkFilterOptions: [
        { text: "Wszyscy kierowcy aktywni", value: "ALL_ACTIVE" },
        { text: "Wszyscy kierowcy nieaktywni", value: "ALL_INACTIVE" },
        { text: "Wynajmujacy nasze auta", value: "RENT_DRIVERS" },
        { text: "Kierowcy - konkretny oddzial", value: "DEPARTMENT" },
        { text: "Pracownicy / Administratorzy", value: "ADMINS" },
        { text: "Wybierz recznie", value: "MANUAL" },
      ],

      emailForm: { subject: "", message: "" },

      loadingHistory: false,
      historyItems: [],
      historyTotal: 0,
      historyOptions: { page: 1, itemsPerPage: 20 },
      historyFilters: { type: "", status: "", search: "" },

      detailDialog: false,
      detailMessage: null,
    };
  },
  computed: {
    canSendSingle() {
      return this.singleForm.email && this.emailForm.subject.trim() && this.emailForm.message.trim();
    },
    canSendBulk() {
      return this.bulkForm.filterType && this.emailForm.subject.trim() && this.emailForm.message.trim() && this.bulkPreview > 0;
    },
    statusFilterOptions() {
      return [
        { text: this.$t('email.all'), value: "" },
        { text: this.getStatusText('SENT'), value: "SENT" },
        { text: this.getStatusText('FAILED'), value: "FAILED" },
        { text: this.getStatusText('PARTIAL'), value: "PARTIAL" },
      ];
    },
    historyHeaders() {
      return [
        { text: this.$t('common.date'), value: "createdAt", width: "160px" },
        { text: this.$t('notificationLogs.type'), value: "type", width: "100px" },
        { text: this.$t('email.recipientColumn'), value: "recipients" },
        { text: this.$t('email.subject'), value: "subject" },
        { text: this.$t('common.status'), value: "status", width: "100px" },
        { text: this.$t('email.actions'), value: "actions", width: "120px", sortable: false },
      ];
    },
  },
  watch: {
    recipientSearch(val) {
      if (!val || val.length < 2) { this.recipientSearchResults = []; return; }
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.searchRecipients(val), 300);
    },
    "historyOptions.page"() { this.loadHistory(); },
    activeTab(val) { if (val === 1) this.loadHistory(); },
  },
  async created() {
    await this.loadDepartments();
  },
  methods: {
    async searchRecipients(search) {
      this.searchingRecipients = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `email/recipients?search=${encodeURIComponent(search)}`,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.recipientSearchResults = res.data.data;
      } catch (e) { console.log(e); } finally { this.searchingRecipients = false; }
    },

    onRecipientSelected(recipient) {
      if (recipient) this.singleForm.email = recipient.email;
    },

    async sendSingle() {
      if (!this.canSendSingle) return;
      this.sending = true;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "email/send",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: {
            driverId: this.singleForm.recipient && this.singleForm.recipient.type === "driver" ? this.singleForm.recipient.id : undefined,
            adminId: this.singleForm.recipient && this.singleForm.recipient.type === "admin" ? this.singleForm.recipient.id : undefined,
            email: this.singleForm.email,
            recipientName: (this.singleForm.recipient && this.singleForm.recipient.name) || this.singleForm.email,
            subject: this.emailForm.subject,
            message: this.emailForm.message,
          },
        });
        Swal.fire({ title: this.$t('email.sentTitle'), text: this.singleForm.email, icon: "success", timer: 3000 });
        this.clearForm();
      } catch (e) {
        Swal.fire(this.$t('common.error'), (e.response && e.response.data && e.response.data.message) || this.$t('email.sendFailed'), "error");
      } finally { this.sending = false; }
    },

    confirmBulkSend() { this.bulkConfirmDialog = true; },

    async sendBulk() {
      this.sending = true;
      try {
        const filter = {
          filterType: this.bulkForm.filterType,
          departmentId: this.bulkForm.departmentId || undefined,
          manualIds: this.bulkForm.filterType === "MANUAL" ? this.bulkForm.manualRecipients.map((r) => r.id) : undefined,
        };
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "email/send-bulk",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { filter, subject: this.emailForm.subject, message: this.emailForm.message },
        });
        this.bulkConfirmDialog = false;
        const data = res.data.data;
        // Masowa wysyłka idzie teraz w tle (limit tempa chroni skrzynkę przed
        // blokadą), więc licznikami chwalimy się dopiero w historii wysyłek.
        if (data.queued) {
          Swal.fire({
            title: this.$t('email.bulkQueuedTitle'),
            text: `${this.$t('email.bulkQueuedText')} (${data.totalRecipients})`,
            icon: "success",
          });
        } else {
          Swal.fire({
            title: this.$t('email.bulkDoneTitle'),
            text: `${this.$t('email.sent')}: ${data.sentCount}/${data.totalRecipients}. ${this.$t('email.errors')}: ${data.failedCount}.`,
            icon: data.failedCount === 0 ? "success" : data.sentCount === 0 ? "error" : "warning",
          });
        }
        this.clearForm();
      } catch (e) {
        Swal.fire(this.$t('common.error'), (e.response && e.response.data && e.response.data.message) || this.$t('email.sendFailed'), "error");
      } finally { this.sending = false; }
    },

    async previewBulkRecipients() {
      if (!this.bulkForm.filterType) { this.bulkPreview = null; return; }
      if (this.bulkForm.filterType === "MANUAL") { this.bulkPreview = this.bulkForm.manualRecipients.length; return; }
      try {
        const filter = { filterType: this.bulkForm.filterType, departmentId: this.bulkForm.departmentId || undefined };
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "email/preview-bulk",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { filter },
        });
        this.bulkPreview = (res.data && res.data.data && res.data.data.count) || 0;
      } catch (e) { this.bulkPreview = 0; }
    },

    async loadHistory() {
      this.loadingHistory = true;
      try {
        const params = new URLSearchParams({
          take: String(this.historyOptions.itemsPerPage),
          skip: String(this.historyOptions.page),
        });
        if (this.historyFilters.type) params.set("type", this.historyFilters.type);
        if (this.historyFilters.status) params.set("status", this.historyFilters.status);
        if (this.historyFilters.search) params.set("search", this.historyFilters.search);
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "email/history?" + params.toString(),
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.historyItems = res.data.data.result;
        this.historyTotal = res.data.data.count;
      } catch (e) { console.log(e); } finally { this.loadingHistory = false; }
    },

    async showMessageDetail(id) {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "email/message/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.detailMessage = res.data.data;
        this.detailDialog = true;
      } catch (e) {
        Swal.fire(this.$t('common.error'), this.$t('email.detailFailed'), "error");
      }
    },

    async resendMessage(id) {
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "email/resend/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        Swal.fire(this.$t('email.resentTitle'), `${this.$t('email.sent')}: ${res.data.data.sentCount}, ${this.$t('email.errors')}: ${res.data.data.failedCount}`, res.data.data.failedCount === 0 ? "success" : "error");
        await this.loadHistory();
      } catch (e) {
        Swal.fire(this.$t('common.error'), (e.response && e.response.data && e.response.data.message) || this.$t('email.sendFailed'), "error");
      }
    },

    async loadDepartments() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "sub-company",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.departments = (res.data.data || []).map((d) => ({ id: d.id, name: d.name }));
      } catch (e) { console.log(e); }
    },

    getFirstRecipientName(item) {
      const recipients = item.recipients;
      if (Array.isArray(recipients) && recipients.length > 0) return recipients[0].name;
      return "-";
    },
    getStatusColor(status) {
      return { SENT: "success", FAILED: "error", PARTIAL: "warning", PENDING: "grey" }[status] || "grey";
    },
    getStatusText(status) {
      return this.$t('email.status.' + status) !== 'email.status.' + status ? this.$t('email.status.' + status) : status;
    },
    clearForm() {
      this.singleForm = { recipient: null, email: "" };
      this.bulkForm = { filterType: null, departmentId: null, manualRecipients: [] };
      this.emailForm = { subject: "", message: "" };
      this.bulkPreview = null;
      this.recipientSearch = "";
      this.recipientSearchResults = [];
    },
  },
};
</script>

<style scoped>
.sms-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 20px; }
.page-title { font-size: 28px; font-weight: 700; margin: 4px 0 0 0; color: #FFFFFF; }
.sms-tabs { background: #232838 !important; border-radius: 12px 12px 0 0; border: 1px solid #2A3142; padding: 0 8px; }
.sms-tabs >>> .v-tabs-bar { background: transparent !important; }
.sms-tabs >>> .v-tab { color: #94A0B8 !important; font-weight: 600; letter-spacing: -0.01em; text-transform: none; }
.sms-tabs >>> .v-tab--active { color: #5478FF !important; }
.sms-tabs >>> .v-tabs-slider { background-color: #5478FF !important; }
.sms-page >>> .v-tabs-items, .sms-page >>> .v-window { background: transparent !important; }
.sms-page >>> .v-card { background: #232838 !important; color: #E6EAF2 !important; border-color: #2A3142 !important; }
.sms-page >>> .v-card__title { color: #FFFFFF !important; }
.sms-page >>> .v-text-field--outlined fieldset { border-color: #2A3142 !important; }
.sms-page >>> .v-text-field input, .sms-page >>> .v-text-field textarea, .sms-page >>> .v-select__selection { color: #E6EAF2 !important; }
.sms-page >>> .v-label { color: #94A0B8 !important; }
.sms-page >>> .v-data-table { background: transparent !important; color: #E6EAF2 !important; }
.sms-page >>> .v-data-table thead tr th { background: #1F2433 !important; color: #94A0B8 !important; border-bottom: 1px solid #2A3142 !important; }
.sms-page >>> .v-data-table tbody tr td { border-bottom: 1px solid #2A3142 !important; color: #E6EAF2 !important; }
</style>
